class PdpNavigationBar extends HTMLElement {
  static DEFAULT_CTA_SELECTOR =
    '.product__info-container .product-form__button';

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._initState();
    this._cacheElements();
    this._removeEmptyLinks();
    this._bindHandlers();
    this._bindEvents();

    this._syncCtaState();
    this._syncHeaderState();
    this._updateBarState();
  }

  disconnectedCallback() {
    this._headerObserver?.disconnect();

    window.removeEventListener('scroll', this._onScrollOrResize);
    window.removeEventListener('resize', this._onScrollOrResize);

    this._links.forEach(link => {
      link.removeEventListener('click', this._onLinkClick);
    });
  }

  _initState() {
    this._ctaSelector =
      this.dataset.ctaSelector?.trim() || PdpNavigationBar.DEFAULT_CTA_SELECTOR;

    this._scrollOffset = Number(this.dataset.scrollOffset) || 0;
    this._ctaAboveViewport = false;
    this._headerUp = false;
  }

  _cacheElements() {
    this._ctaEl = document.querySelector(this._ctaSelector);
    this._headerEl = document.querySelector('.header__wrap');
    this._linksWrap = this.querySelector('[data-nav-links]');
    this._links = [...this.querySelectorAll('[data-nav-link]')];
  }

  _removeEmptyLinks() {
    this._links = this._links.filter(link => {
      const target = this._getTargetElement(link.dataset.target);

      if (this._isTargetEmpty(target)) {
        link.remove();
        return false;
      }

      return true;
    });

    if (this._links.length > 0) return;

    this._linksWrap?.remove();
    this.querySelector('nav')?.remove();
  }

  _getTargetElement(targetSelector) {
    if (!targetSelector) return null;

    return document.querySelector(targetSelector);
  }

  _isTargetEmpty(target) {
    if (!target) return true;

    return (
      target.children.length === 0 && target.textContent.trim().length === 0
    );
  }

  _bindHandlers() {
    this._onScrollOrResize = () => this._syncCtaState();
    this._onLinkClick = event => this._handleLinkClick(event);
    this._onHeaderMutation = () => this._handleHeaderMutation();
  }

  _bindEvents() {
    this._bindCtaTracking();
    this._bindHeaderSync();
    this._bindLinkClicks();
  }

  _bindCtaTracking() {
    window.addEventListener('scroll', this._onScrollOrResize, {
      passive: true
    });

    window.addEventListener('resize', this._onScrollOrResize, {
      passive: true
    });
  }

  _bindHeaderSync() {
    if (!this._headerEl) return;

    this._handleHeaderMutation();

    this._headerObserver = new MutationObserver(this._onHeaderMutation);
    this._headerObserver.observe(this._headerEl, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  _bindLinkClicks() {
    this._links.forEach(link => {
      link.addEventListener('click', this._onLinkClick);
    });
  }

  _handleHeaderMutation() {
    if (document.body.classList.contains('hide-header')) return;

    this._syncHeaderState();
  }

  _syncCtaState() {
    const isAboveViewport = this._isCtaAboveViewport();

    if (this._ctaAboveViewport === isAboveViewport) return;

    this._ctaAboveViewport = isAboveViewport;
    this._updateBarState();
  }

  _syncHeaderState() {
    const isHeaderUp = this._headerEl?.classList.contains('header-up') ?? false;

    if (this._headerUp === isHeaderUp) return;

    this._headerUp = isHeaderUp;
    this._updateBarState();
  }

  _isCtaAboveViewport() {
    if (!this._ctaEl) {
      return this._isFallbackAboveViewport();
    }

    const rect = this._ctaEl.getBoundingClientRect();
    // CTA is hidden (e.g. display:none on OOS) — fall back to the product info container
    if (rect.width === 0 && rect.height === 0) {
      return this._isFallbackAboveViewport();
    }

    return rect.bottom < 0;
  }

  _isFallbackAboveViewport() {
    const fallback = document.querySelector('.product__info-container');
    if (!fallback) return true;

    return fallback.getBoundingClientRect().bottom < 0;
  }

  _handleLinkClick(event) {
    const link = event.currentTarget;
    const targetSelector = link.dataset.target;

    if (!targetSelector) return;

    const target = this._getTargetElement(targetSelector);
    if (!target) return;

    event.preventDefault();

    this._trackQuickNavigationClick(link);
    this._setActiveLink(link);
    this._scrollToTarget(target);
  }

  _trackQuickNavigationClick(link) {
    const index = this._links.indexOf(link) + 1;

    if (index < 1) return;

    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      event: 'custom_event',
      event_name: 'quick_navigation_click',
      parameter: JSON.stringify({
        to: `${index}`
      })
    });
  }

  _setActiveLink(activeLink) {
    this._links.forEach(link => {
      link.classList.toggle('active', link === activeLink);
    });
  }

  _getScrollOffset() {
    if (this._scrollOffset > 0) return this._scrollOffset;

    return this._headerEl?.offsetHeight ?? 104;
  }

  _scrollToTarget(target) {
    const offset = this._getScrollOffset();
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: 'smooth'
    });
  }

  _updateBarState() {
    this.classList.toggle('is-enabled', this._ctaAboveViewport);
    this.classList.toggle(
      'is-scroll-hidden',
      this._ctaAboveViewport && this._headerUp
    );
  }
}

if (!customElements.get('pdp-navigation-bar')) {
  customElements.define('pdp-navigation-bar', PdpNavigationBar);
}
